/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Button, Checkbox, Pagination, Table } from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ColumnDef, Row } from "@tanstack/react-table";
import { IBill } from "../../types/billing.types";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import ListState from "../../../../components/ListState/ListState";
import {
    cn,
    formatDate,
    formatNoteType,
    getTotalAmountOfTableRowsSelected,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import {
    useFetchPaymentCards,
    useGetOutstandingBills,
} from "../../hooks/billing.queries";
import "./OutstandingBills.css";
import PaymentsSummary from "../../../payments/components/PaymentsSummary/PaymentsSummary";

export default function OutstandingBills() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedData, setGetSelectedData] = React.useState<Row<IBill>[]>(
        []
    );

    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter = Number(searchParams.get("limit")) || 20;

    const { data, error, isLoading, isSuccess } = useGetOutstandingBills({
        page: pageFilter,
        limit: limitFilter,
    });

    const { data: cards } = useFetchPaymentCards();

    const navigateToPayment = (invoiceIds: string[]) => {
        invoiceIds.forEach((id) => {
            searchParams.append("selectedInvoiceIds", id);
        });
        searchParams.set("invoicePage", String(pageFilter));
        if (cards?.data.length === 0) {
            searchParams.set("payment_method", "new_card");
        }
        navigate(`/payment-page?${searchParams.toString()}`);
    };

    const columns: ColumnDef<IBill>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "date_of_service",
            header: "Date of Service",
            cell: ({ row }) => formatDate(row.original.date_of_service),
        },
        {
            accessorKey: "provider",
            header: "Therapist",
            cell: ({ row }) =>
                `${row.original.provider.first_name} ${row.original.provider.last_name}`,
        },
        {
            accessorKey: "supervisor",
            header: "Supervisor",
            cell: ({ row }) =>
                row.original.supervisor
                    ? `${row.original.supervisor.first_name} ${row.original.supervisor.last_name}`
                    : "--",
        },
        {
            accessorKey: "note_type",
            header: "SERVICE",
            cell: ({ row }) => (
                <span className=" capitalize">
                    {formatNoteType(row.original.note_type)}
                </span>
            ),
        },
        {
            accessorKey: "cpt_code",
            header: "Service Code",
            cell: ({ row }) =>
                row.original.cpt_code
                    ? removeEnumUnderscore(row.original.cpt_code)
                    : "--",
        },
        {
            header: "SESSION FEE",
            accessorKey: "coinsurance_amount",
            cell: ({ row }) => {
                const sessionFee =
                    Math.abs(row.original.coinsurance_amount || 0) -
                    Math.abs(row.original.coinsurance_amount_collected || 0);

                const isNegative = sessionFee < 0;
                const displayAmount = showMoneyInAppFormat(
                    Math.abs(sessionFee)
                );

                return (
                    <span className={cn({ "text-danger": isNegative })}>
                        {isNegative ? `(${displayAmount})` : displayAmount}
                    </span>
                );
            },
        },

        {
            accessorKey: "pr100_amount",
            header: "INSURANCE REIMBURSEMENT",
            cell: ({ row }) => {
                return (
                    <span
                        className={cn({
                            "text-danger": row.original.pr100_outstanding < 0,
                        })}
                    >
                        {row.original.pr100_outstanding < 0
                            ? `(${showMoneyInAppFormat(
                                  row.original.pr100_outstanding
                              )})`
                            : showMoneyInAppFormat(
                                  Math.abs(row.original.pr100_outstanding)
                              )}
                    </span>
                );
            },
        },
        {
            accessorKey: "amount_outstanding",
            header: "BALANCE",
            cell: ({ row }) => {
                const sessionFee =
                    Math.abs(row.original.coinsurance_amount || 0) -
                    Math.abs(row.original.coinsurance_amount_collected || 0);

                const insuranceReimbursement =
                    Math.abs(row.original.pr100_amount || 0) -
                    Math.abs(row.original.pr100_collected || 0);

                const adjustedSessionFee = sessionFee < 0 ? 0 : sessionFee;
                const adjustedInsuranceReimbursement =
                    insuranceReimbursement < 0 ? 0 : insuranceReimbursement;

                const balance =
                    adjustedSessionFee + adjustedInsuranceReimbursement;

                return showMoneyInAppFormat(balance);
            },
        },
        {
            accessorKey: "id",
            header: "",
            cell: ({ row }) => (
                <Button
                    onClick={() => navigateToPayment([row.original.invoice_id])}
                >
                    Pay now
                </Button>
            ),
        },
    ];

    const invoiceIdsFromSelectedData =
        getSelectedData &&
        getSelectedData.map((item) => item.original.invoice_id);

    const handleBulkPay = () => {
        if (
            !invoiceIdsFromSelectedData ||
            invoiceIdsFromSelectedData.length === 0
        )
            return;
        navigateToPayment(invoiceIdsFromSelectedData);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    return (
        <div>
            {error && (
                <ListState
                    isError
                    stateHelperText="Please try again later"
                    errorMsg={
                        error.response?.data.message ||
                        "Error loading outstanding bills"
                    }
                />
            )}

            {isLoading && (
                <Skeleton type="table" containerTestId="bills-loader" />
            )}

            {isSuccess && data && (
                <>
                    {data.data.length === 0 && (
                        <ListState
                            emptyMessage="No outstanding bills yet"
                            stateHelperText="Outstanding bills will appear here when available"
                            isError={false}
                        />
                    )}
                    {data.data.length > 0 && (
                        <div>
                            {data.summary ? (
                                <div className="mb-12">
                                    <PaymentsSummary
                                        summaryData={data.summary}
                                    />
                                </div>
                            ) : null}
                            {getTotalAmountOfTableRowsSelected(rowSelection)
                                .amount > 0 && (
                                <div className="flex items-center gap-x-5 mb-6">
                                    <div className="text-base font-medium">
                                        {
                                            getTotalAmountOfTableRowsSelected(
                                                rowSelection
                                            ).amountString
                                        }
                                        :
                                    </div>
                                    <Button onClick={handleBulkPay}>
                                        Pay bills
                                    </Button>
                                </div>
                            )}
                            <Table
                                columns={columns}
                                data={data.data}
                                className="outstandingbills"
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                setGetSelectedData={setGetSelectedData}
                                hasPagination={data.total_count > 20}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
